import moment from "moment-timezone";

/**
 * Converts an array of objects to CSV.
 * @param {Array} data - Array of objects to convert.
 * @param {Array} columns - Column configuration with header and accessor.
 * @returns {string} - CSV content.
 */
export function convertToCSV(data, columns) {
  if (!data || data.length === 0) {
    return "";
  }

  // Create CSV header
  const headers = columns.map(col => `"${col.header}"`).join(",");
  
  // Create data rows
  const rows = data.map(item => {
    return columns.map(col => {
      let value = col.accessor(item);
      
      // Handle null/undefined values
      if (value === null || value === undefined) {
        value = "";
      }
      
      // Convert to string and escape quotes
      value = String(value).replace(/"/g, '""');
      
      return `"${value}"`;
    }).join(",");
  });

  return [headers, ...rows].join("\n");
}

/**
 * Triggers a CSV file download.
 * @param {string} csvContent - CSV content.
 * @param {string} filename - Target filename.
 */
export function downloadCSV(csvContent, filename) {
  // Create a blob with the CSV content
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  
  // Create a temporary download link
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  
  // Append to DOM, click, then remove
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Column configuration for bookings CSV export.
 */
export const bookingExportColumns = [
  {
    header: "name",
    accessor: (booking) => booking.name || `${booking.firstname || ""} ${booking.lastname || ""}`.trim()
  },
  {
    header: "vehicle category", 
    accessor: (booking) => booking.vehicle || ""
  },
  {
    header: "booking date",
    accessor: (booking) => {
      // created_at can be:
      // - a UNIX timestamp in seconds (number)
      // - a formatted string set by table transformation (e.g. "YYYY-MM-DD HH:mm")
      // Fallback: return empty string if not parseable
      if (typeof booking.created_at === "number" && booking.created_at > 0) {
        return moment(booking.created_at * 1000)
          .tz("Europe/Paris")
          .format("DD/MM/YYYY HH:mm");
      }

      if (typeof booking.created_at === "string" && booking.created_at.trim().length > 0) {
        const m = moment.tz(booking.created_at, "YYYY-MM-DD HH:mm", "Europe/Paris");
        return m.isValid() ? m.format("DD/MM/YYYY HH:mm") : "";
      }

      return "";
    }
  },
  {
    header: "pick up date",
    accessor: (booking) => {
      if (booking.date && booking.time) {
        return moment(booking.date * 1000).tz("Europe/Paris").format("DD/MM/YYYY") + " " + booking.time;
      }
      return "";
    }
  },
  {
    header: "num passengers",
    accessor: (booking) => booking.passengers || ""
  },
  {
    header: "user reservation id",
    accessor: (booking) => booking.id || ""
  },
  {
    header: "pick up address",
    accessor: (booking) => booking.pickup?.address || ""
  },
  {
    header: "drop off address", 
    accessor: (booking) => booking.dropoff?.address || ""
  },
  {
    header: "pricing",
    accessor: (booking) => booking.amount || 0
  },
  {
    header: "kilometrage",
    accessor: (booking) => {
      if (booking.distance) {
        // Convert from meters to kilometers and round to 2 decimal places
        const distanceInKm = (parseFloat(booking.distance) / 1000).toFixed(2);
        return distanceInKm;
      }
      return "";
    }
  }
];

/**
 * Exports filtered bookings to CSV.
 * @param {Array} bookings - Bookings to export.
 * @param {Object} filters - Applied filters (used for filename).
 */
export function exportBookingsToCSV(bookings, filters = {}) {
  if (!bookings || bookings.length === 0) {
    alert("Aucune donnée à exporter");
    return;
  }

  // Generate CSV content
  const csvContent = convertToCSV(bookings, bookingExportColumns);
  
  // Generate filename
  const now = moment().tz("Europe/Paris").format("YYYY-MM-DD_HH-mm-ss");
  let filename = `export_bookings_${now}.csv`;
  
  // Add filter info to filename if present
  if (filters.company) {
    filename = `export_bookings_${filters.company.name}_${now}.csv`;
  }
  
  // Trigger file download
  downloadCSV(csvContent, filename);
}
